import React, { useState, useEffect } from 'react';
import apiService from "services/api/apiService";
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Switch,
  Select,
  InputNumber,
  message,
  Divider,
  Alert,
  Upload,
  Progress,
  Tabs,
  Table,
  Tag,
  Modal
} from 'antd';
import {
  SettingOutlined,
  SecurityScanOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  MonitorOutlined,
  MailOutlined,
  BellOutlined,
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface SystemConfig {
  // General Settings
  system_name: string;
  system_description: string;
  admin_email: string;
  timezone: string;
  date_format: string;
  time_format: string;
  
  // Security Settings
  session_timeout: number;
  password_min_length: number;
  require_2fa: boolean;
  login_attempts_limit: number;
  account_lockout_duration: number;
  
  // Email Settings
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_encryption: string;
  from_email: string;
  from_name: string;
  
  // Notification Settings
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  notification_frequency: string;
  
  // Storage Settings
  max_file_size: number;
  allowed_file_types: string[];
  storage_provider: string;
  backup_frequency: string;
  
  // Performance Settings
  cache_enabled: boolean;
  cache_duration: number;
  api_rate_limit: number;
  maintenance_mode: boolean;
}

interface BackupInfo {
  id: string;
  filename: string;
  size: number;
  created_at: string;
  type: 'manual' | 'automatic';
  status: 'completed' | 'in_progress' | 'failed';
}

interface Props {
  data: any;
  onRefresh: () => void;
}

const SystemConfiguration: React.FC<Props> = ({ data, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [form] = Form.useForm();

  const [systemConfig, setSystemConfig] = useState<SystemConfig>({
    // General Settings
    system_name: 'Nexus HR Management System',
    system_description: 'Comprehensive HR Management Platform',
    admin_email: 'admin@nexus-hr.com',
    timezone: 'Africa/Lagos',
    date_format: 'YYYY-MM-DD',
    time_format: '24h',
    
    // Security Settings
    session_timeout: 60,
    password_min_length: 8,
    require_2fa: false,
    login_attempts_limit: 5,
    account_lockout_duration: 30,
    
    // Email Settings
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    from_email: '',
    from_name: 'Nexus HR System',
    
    // Notification Settings
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    notification_frequency: 'immediate',
    
    // Storage Settings
    max_file_size: 10,
    allowed_file_types: ['pdf', 'doc', 'docx', 'jpg', 'png'],
    storage_provider: 'local',
    backup_frequency: 'daily',
    
    // Performance Settings
    cache_enabled: true,
    cache_duration: 3600,
    api_rate_limit: 1000,
    maintenance_mode: false
  });

  const [backups, setBackups] = useState<BackupInfo[]>([
    {
      id: '1',
      filename: 'nexus-hr-backup-20251107-120000.sql',
      size: 15728640, // 15MB
      created_at: '2025-11-07T12:00:00Z',
      type: 'automatic',
      status: 'completed'
    }
  ]);

  const [systemStats, setSystemStats] = useState({
    uptime: '7 days, 14 hours',
    cpu_usage: 25,
    memory_usage: 60,
    disk_usage: 45,
    active_sessions: 12,
    database_size: '2.5 GB',
    cache_hit_rate: 85
  });

  const fetchSystemConfig = async () => {
    try {
      setLoading(true);
      // API call will be implemented
      // For now using default settings
    } catch (error) {
      message.error('Failed to load system configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemConfig();
    form.setFieldsValue(systemConfig);
  }, []);

  const handleSaveConfig = async (values: any) => {
    try {
      setLoading(true);
      const tenantId = apiService.getTenantId();
      const response = await fetch(
        `https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${tenantId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update_company_settings',
            tenant_id: tenantId,
            settings: {
              companyName:    values.system_name,
              companyEmail:   values.admin_email,
              timezone:       values.timezone,
              dateFormat:     values.date_format,
            }
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        setSystemConfig({ ...systemConfig, ...values });
        // Refresh token so header shows new company name immediately
        const token = localStorage.getItem('hr_access_token');
        if (token) {
          try {
            const decoded = JSON.parse(atob(token));
            decoded.organization_name = values.system_name;
            decoded.company_name = values.system_name;
            const newToken = btoa(JSON.stringify(decoded));
            localStorage.setItem('hr_access_token', newToken);
          } catch (e) { /* token refresh failed silently */ }
        }
        message.success('Company settings saved successfully');
        onRefresh();
      } else {
        message.error(data.error || 'Failed to save settings');
      }
    } catch (error) {
      message.error('Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestEmailLoading(true);
      // Simulate email test
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success('Test email sent successfully!');
    } catch (error) {
      message.error('Failed to send test email');
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      // Simulate backup creation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const newBackup: BackupInfo = {
        id: Date.now().toString(),
        filename: `nexus-hr-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.sql`,
        size: Math.floor(Math.random() * 20000000) + 10000000,
        created_at: new Date().toISOString(),
        type: 'manual',
        status: 'completed'
      };
      
      setBackups([newBackup, ...backups]);
      message.success('Backup created successfully!');
    } catch (error) {
      message.error('Failed to create backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const backupColumns = [
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      render: (filename: string) => (
        <Text code>{filename}</Text>
      )
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (size: number) => formatFileSize(size)
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'automatic' ? 'blue' : 'green'}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'completed' ? 'green' : status === 'in_progress' ? 'orange' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: BackupInfo) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} size="small">
            Download
          </Button>
          <Button type="text" icon={<DeleteOutlined />} size="small" danger>
            Delete
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Tabs defaultActiveKey="general">
        {/* General Settings Tab */}
        <TabPane 
          tab={
            <span>
              <SettingOutlined />
              General
            </span>
          } 
          key="general"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveConfig}
            initialValues={systemConfig}
          >
            <Card title="System Information" style={{ marginBottom: '24px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="system_name"
                    label="Company Name"
                    rules={[{ required: true, message: 'Please enter system name' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="admin_email"
                    label="Admin Email"
                    rules={[
                      { required: true, message: 'Please enter admin email' },
                      { type: 'email', message: 'Please enter valid email' }
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="system_description"
                label="System Description"
              >
                <TextArea rows={3} />
              </Form.Item>

              {/* Logo Upload Section */}
              <div style={{ marginBottom: "24px" }}>
                <Text strong>Company Logo</Text>
                <div style={{ marginTop: "8px" }}>
                  <Upload
                    name="logo"
                    listType="picture-card"
                    showUploadList={false}
                    action="/backend/hr-api/settings/logo-upload.php"
                    data={{ tenant_id: apiService.getTenantId() }}
                    beforeUpload={(file) => {
                      const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
                      if (!isJpgOrPng) message.error("Only JPG/PNG files allowed!");
                      const isLt2M = file.size / 1024 / 1024 < 2;
                      if (!isLt2M) message.error("Image must be smaller than 2MB!");
                      return isJpgOrPng && isLt2M;
                    }}
                    onChange={(info) => {
                      if (info.file.status === "done") {
                        message.success("Logo uploaded successfully!");
                      } else if (info.file.status === "error") {
                        message.error("Logo upload failed!");
                      }
                    }}
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Upload Logo</div>
                      <div style={{ fontSize: "12px", color: "#999" }}>JPG/PNG, max 2MB</div>
                    </div>
                  </Upload>
                </div>
              </div>

              <Form.Item
                name="system_description"
                label="System Description"
              >
                <TextArea rows={3} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="timezone"
                    label="Timezone"
                    rules={[{ required: true, message: 'Please select timezone' }]}
                  >
                    <Select>
                      <Option value="Africa/Lagos">Africa/Lagos (WAT)</Option>
                      <Option value="UTC">UTC</Option>
                      <Option value="America/New_York">America/New_York (EST)</Option>
                      <Option value="Europe/London">Europe/London (GMT)</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="date_format"
                    label="Date Format"
                  >
                    <Select>
                      <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                      <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                      <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="time_format"
                    label="Time Format"
                  >
                    <Select>
                      <Option value="24h">24 Hour</Option>
                      <Option value="12h">12 Hour</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Storage Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="max_file_size"
                    label="Max File Size (MB)"
                  >
                    <InputNumber min={1} max={100} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="storage_provider"
                    label="Storage Provider"
                  >
                    <Select>
                      <Option value="local">Local Storage</Option>
                      <Option value="aws_s3">AWS S3</Option>
                      <Option value="google_cloud">Google Cloud</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="allowed_file_types"
                label="Allowed File Types"
              >
                <Select mode="tags">
                  <Option value="pdf">PDF</Option>
                  <Option value="doc">DOC</Option>
                  <Option value="docx">DOCX</Option>
                  <Option value="jpg">JPG</Option>
                  <Option value="png">PNG</Option>
                  <Option value="xlsx">XLSX</Option>
                </Select>
              </Form.Item>
            </Card>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save General Settings
              </Button>
            </Form.Item>
          </Form>
        </TabPane>

        {/* Security Tab */}
        <TabPane 
          tab={
            <span>
              <SecurityScanOutlined />
              Security
            </span>
          } 
          key="security"
        >
          <Card title="Authentication Settings" style={{ marginBottom: '24px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="session_timeout"
                  label="Session Timeout (minutes)"
                >
                  <InputNumber min={15} max={480} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="password_min_length"
                  label="Minimum Password Length"
                >
                  <InputNumber min={6} max={32} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="login_attempts_limit"
                  label="Login Attempts Limit"
                >
                  <InputNumber min={3} max={10} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="account_lockout_duration"
                  label="Lockout Duration (minutes)"
                >
                  <InputNumber min={5} max={120} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="require_2fa"
              label="Require Two-Factor Authentication"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Card>
        </TabPane>

        {/* Email Configuration Tab */}
        <TabPane 
          tab={
            <span>
              <MailOutlined />
              Email
            </span>
          } 
          key="email"
        >
          <Card title="SMTP Configuration" style={{ marginBottom: '24px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="smtp_host"
                  label="SMTP Host"
                >
                  <Input placeholder="smtp.gmail.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="smtp_port"
                  label="SMTP Port"
                >
                  <InputNumber min={1} max={65535} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="smtp_username"
                  label="SMTP Username"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="smtp_password"
                  label="SMTP Password"
                >
                  <Input.Password />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="smtp_encryption"
                  label="Encryption"
                >
                  <Select>
                    <Option value="none">None</Option>
                    <Option value="tls">TLS</Option>
                    <Option value="ssl">SSL</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="from_email"
                  label="From Email"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="from_name"
                  label="From Name"
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button 
                type="primary" 
                onClick={handleTestEmail}
                loading={testEmailLoading}
              >
                Test Email Configuration
              </Button>
            </Form.Item>
          </Card>
        </TabPane>

        {/* System Monitoring Tab */}
        <TabPane 
          tab={
            <span>
              <MonitorOutlined />
              Monitoring
            </span>
          } 
          key="monitoring"
        >
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={8}>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>CPU Usage</Title>
                  <Progress 
                    type="circle" 
                    percent={systemStats.cpu_usage}
                    strokeColor={systemStats.cpu_usage > 80 ? '#ff4d4f' : '#52c41a'}
                  />
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>Memory Usage</Title>
                  <Progress 
                    type="circle" 
                    percent={systemStats.memory_usage}
                    strokeColor={systemStats.memory_usage > 80 ? '#ff4d4f' : '#52c41a'}
                  />
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>Disk Usage</Title>
                  <Progress 
                    type="circle" 
                    percent={systemStats.disk_usage}
                    strokeColor={systemStats.disk_usage > 80 ? '#ff4d4f' : '#52c41a'}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          <Card title="System Statistics">
            <Row gutter={16}>
              <Col span={8}>
                <div>
                  <Text strong>System Uptime:</Text>
                  <br />
                  <Text>{systemStats.uptime}</Text>
                </div>
              </Col>
              <Col span={8}>
                <div>
                  <Text strong>Active Sessions:</Text>
                  <br />
                  <Text>{systemStats.active_sessions}</Text>
                </div>
              </Col>
              <Col span={8}>
                <div>
                  <Text strong>Database Size:</Text>
                  <br />
                  <Text>{systemStats.database_size}</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </TabPane>

        {/* Backup & Maintenance Tab */}
        <TabPane 
          tab={
            <span>
              <DatabaseOutlined />
              Backup
            </span>
          } 
          key="backup"
        >
          <Card 
            title="Database Backups"
            extra={
              <Button 
                type="primary" 
                icon={<CloudUploadOutlined />}
                onClick={handleCreateBackup}
                loading={backupLoading}
              >
                Create Backup
              </Button>
            }
            style={{ marginBottom: '24px' }}
          >
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={8}>
                <Form.Item label="Backup Frequency">
                  <Select defaultValue="daily">
                    <Option value="hourly">Hourly</Option>
                    <Option value="daily">Daily</Option>
                    <Option value="weekly">Weekly</Option>
                    <Option value="monthly">Monthly</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Retention Period">
                  <Select defaultValue="30">
                    <Option value="7">7 days</Option>
                    <Option value="30">30 days</Option>
                    <Option value="90">90 days</Option>
                    <Option value="365">1 year</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Maintenance Mode">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Table
              columns={backupColumns}
              dataSource={backups}
              rowKey="id"
              pagination={{
                pageSize: 5,
                showSizeChanger: false
              }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default SystemConfiguration;
